import {
  MESES_VALIDADE_MATRIZ,
  NOME_MODELO_MATRIZ,
  UNIDADES,
  competenciaMatriz,
  mesesDesde,
  modeloMatriz,
  parsearMatriz,
  statusMatriz,
} from "@caint/data";
import type { ItemMatriz, MatrizUnidade, ResumoMatrizUnidade } from "@caint/shared";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { mensagemAmigavel } from "../lib/erros.ts";
import type { MatrizApi } from "../state/useAppState.ts";
import { COR_STATUS, ListaQuadros, SeloQuadro } from "./matriz/ListaQuadros.tsx";

function contarPorArea(itens: readonly ItemMatriz[]): [string, number][] {
  const m = new Map<string, number>();
  for (const i of itens) {
    m.set(i.area, (m.get(i.area) ?? 0) + i.quantitativo);
  }
  return [...m].sort((a, b) => b[1] - a[1]);
}

/** Baixa o modelo .xlsx (mesmas abas/colunas que a leitura espera). */
async function baixarModelo() {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();
  for (const aba of modeloMatriz()) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aba.linhas as unknown[][]), aba.nome);
  }
  XLSX.writeFile(wb, NOME_MODELO_MATRIZ);
}

function BotaoModelo() {
  return (
    <button type="button" className="m-btn" onClick={() => void baixarModelo()}>
      ↓ Baixar modelo
    </button>
  );
}

/**
 * Matriz da Unidade (admin/coord). Duas telas: a lista dos quadros da rede
 * (quem tem, de que mês/ano, quem venceu) e, ao escolher uma unidade, o painel
 * de envio — lê o Excel no navegador (SheetJS, import dinâmico), mostra o
 * preview e grava o roster. O modelo da planilha sai daqui mesmo, para o gestor
 * não ter que adivinhar o formato.
 */
export function MatrizUpload({ matrizApi, onBack }: { matrizApi: MatrizApi; onBack?: () => void }) {
  const [unidadeId, setUnidadeId] = useState("");
  const [itens, setItens] = useState<ItemMatriz[] | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [arquivoNome, setArquivoNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [existente, setExistente] = useState<MatrizUnidade | null>(null);
  const [resumos, setResumos] = useState<ResumoMatrizUnidade[]>([]);
  const [erroLista, setErroLista] = useState<string | null>(null);
  // Congelado no primeiro render: a régua "atualizado/desatualizado" não pode
  // mudar de resposta entre renders da mesma tela.
  const [agora] = useState(() => new Date());

  const unidade = UNIDADES.find((u) => u.id === unidadeId) ?? null;

  const recarregarResumos = useCallback(() => {
    return matrizApi
      .listar()
      .then((r) => {
        setResumos(r);
        setErroLista(null);
      })
      .catch((err: unknown) => setErroLista(mensagemAmigavel(err)));
  }, [matrizApi]);

  useEffect(() => {
    void recarregarResumos();
  }, [recarregarResumos]);

  useEffect(() => {
    if (!unidadeId) {
      setExistente(null);
      return;
    }
    let vivo = true;
    setExistente(null);
    matrizApi
      .obter(unidadeId)
      .then((m) => vivo && setExistente(m))
      .catch(() => vivo && setExistente(null));
    return () => {
      vivo = false;
    };
  }, [unidadeId, matrizApi]);

  function escolher(id: string) {
    setUnidadeId(id);
    setItens(null);
    setErro(null);
    setMsg(null);
  }

  async function aoArquivo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setErro(null);
    setMsg(null);
    setItens(null);
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const acharAba = (re: RegExp) => wb.SheetNames.find((n) => re.test(n));
      const abaQuant = acharAba(/quantitativ/i);
      if (!abaQuant) {
        setErro('Não encontrei a aba "Quantitativos" na planilha. Baixe o modelo e use o mesmo formato.');
        return;
      }
      const linhas = (nome: string | undefined): unknown[][] => {
        const ws = nome ? wb.Sheets[nome] : undefined;
        return ws ? (XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][]) : [];
      };
      const r = parsearMatriz(linhas(abaQuant), linhas(acharAba(/reporte/i)));
      if (r.itens.length === 0) {
        setErro("Nenhum cargo válido encontrado na planilha.");
        return;
      }
      setItens(r.itens);
      setAvisos(r.avisos);
      setArquivoNome(file.name);
    } catch (err) {
      setErro(mensagemAmigavel(err));
    } finally {
      e.target.value = ""; // permite reescolher o mesmo arquivo
    }
  }

  async function salvar() {
    if (!itens || !unidadeId) {
      return;
    }
    setSalvando(true);
    setMsg(null);
    setErro(null);
    try {
      await matrizApi.salvar(unidadeId, { arquivoNome, itens });
      setMsg(`Matriz salva: ${itens.length} cargos, ${total} respondentes esperados.`);
      setItens(null);
      setExistente(await matrizApi.obter(unidadeId));
      await recarregarResumos();
    } catch (err) {
      setErro(mensagemAmigavel(err));
    } finally {
      setSalvando(false);
    }
  }

  const total = itens?.reduce((s, i) => s + i.quantitativo, 0) ?? 0;
  const porArea = itens ? contarPorArea(itens) : [];
  const status = statusMatriz(existente?.atualizadoEm, agora);
  const mesesQuadro = existente ? mesesDesde(existente.atualizadoEm, agora) : null;
  const vencidos = resumos.filter((r) => statusMatriz(r.atualizadoEm, agora) === "desatualizado").length;

  /* ---------- Tela 1: a rede inteira, com quem tem quadro e de quando ---------- */
  if (!unidade) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {onBack && (
          <button type="button" className="m-btn" style={{ marginBottom: 10 }} onClick={onBack}>
            ← Voltar
          </button>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div>
            <h2 style={{ margin: "0 0 4px" }}>Matriz da Unidade</h2>
            <p className="spec" style={{ margin: 0 }}>
              O quadro esperado de cada unidade — denominador da cobertura do IMGR e origem dos cargos do
              formulário. Escolha a unidade para enviar ou atualizar.
            </p>
          </div>
          <BotaoModelo />
        </div>

        <div className="m-kpis" style={{ margin: "14px 0 4px" }}>
          <div className="m-kpi">
            <b>
              {resumos.length}/{UNIDADES.length}
            </b>
            <span>unidades com quadro</span>
          </div>
          <div className="m-kpi">
            <b style={{ color: vencidos > 0 ? "var(--warning)" : undefined }}>{vencidos}</b>
            <span>desatualizados ({MESES_VALIDADE_MATRIZ}+ meses)</span>
          </div>
        </div>

        {erroLista && (
          <p className="spec" style={{ color: "var(--danger)" }}>
            Não foi possível carregar a situação dos quadros: {erroLista}
          </p>
        )}

        <ListaQuadros resumos={resumos} agora={agora} onEscolher={escolher} />
      </div>
    );
  }

  /* ---------- Tela 2: a unidade escolhida ---------- */
  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <button type="button" className="m-btn" style={{ marginBottom: 10 }} onClick={() => escolher("")}>
        ← Todas as unidades
      </button>

      <h2 style={{ margin: "0 0 2px" }}>{unidade.nome}</h2>
      <p className="spec" style={{ marginBottom: 12 }}>
        {unidade.id}
        {unidade.municipio ? ` · ${unidade.municipio}` : ""}
      </p>

      <div className="m-card pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <SeloQuadro resumo={existente ?? undefined} agora={agora} />
          <span style={{ fontWeight: 700, color: COR_STATUS[status] }}>
            {status === "ausente"
              ? "Nenhum quadro enviado"
              : status === "desatualizado"
                ? "Quadro desatualizado"
                : "Quadro atualizado"}
          </span>
        </div>
        {existente ? (
          <div className="spec" style={{ marginTop: 6, lineHeight: 1.6 }}>
            <div>
              Enviado em <b>{competenciaMatriz(existente.atualizadoEm)}</b>
              {mesesQuadro !== null && mesesQuadro > 0 && ` · há ${mesesQuadro} mês(es)`}
              {existente.arquivoNome ? ` · ${existente.arquivoNome}` : ""}
            </div>
            <div>
              {existente.itens.length} cargos · {existente.itens.reduce((s, i) => s + i.quantitativo, 0)}{" "}
              respondentes esperados
            </div>
            <div>
              {status === "desatualizado"
                ? `Quadro com ${MESES_VALIDADE_MATRIZ} meses ou mais — confirme com a unidade antes de usar na cobertura.`
                : "Enviar uma nova planilha substitui o quadro atual."}
            </div>
          </div>
        ) : (
          <div className="spec" style={{ marginTop: 6 }}>
            Sem quadro no sistema: a cobertura do IMGR desta unidade fica sem denominador.
          </div>
        )}
      </div>

      <div className="m-card pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <label htmlFor="mu-arquivo" className="spec" style={{ fontWeight: 700 }}>
            {existente ? "Enviar quadro atualizado (.xlsx)" : "Enviar quadro (.xlsx)"}
          </label>
          <BotaoModelo />
        </div>
        <p className="spec" style={{ margin: "6px 0 0" }}>
          Abas <b>Quantitativos</b> + <b>Matriz de Reporte</b>. O modelo traz as colunas exatamente como o
          sistema lê — não renomeie as abas nem mude a ordem das colunas.
        </p>
        <input
          id="mu-arquivo"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => void aoArquivo(e)}
          style={{ display: "block", marginTop: 8 }}
        />
      </div>

      {erro && <p style={{ color: "var(--danger)" }}>{erro}</p>}
      {msg && <p style={{ color: "var(--mat-3)", fontWeight: 600 }}>✓ {msg}</p>}

      {itens && (
        <div className="m-card pad">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Pré-visualização — {itens.length} cargos · <b>{total}</b> respondentes esperados
          </div>
          <div style={{ display: "grid", gap: 4, marginBottom: 10 }}>
            {porArea.map(([area, n]) => (
              <div key={area} className="spec" style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{area}</span>
                <b>{n}</b>
              </div>
            ))}
          </div>
          {avisos.length > 0 && (
            <div className="spec" style={{ color: "var(--warning)", marginBottom: 10 }}>
              {avisos.length} linha(s) ignorada(s): {avisos.slice(0, 4).join(" · ")}
              {avisos.length > 4 ? "…" : ""}
            </div>
          )}
          <button type="button" className="m-btn primary" disabled={salvando} onClick={() => void salvar()}>
            {salvando ? "Salvando…" : existente ? "Substituir quadro desta unidade" : "Salvar quadro"}
          </button>
        </div>
      )}
    </div>
  );
}
